'use client';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trash2Icon, PencilIcon, EditIcon, XIcon, Lock } from 'lucide-react';
import type { Tournament } from '@/types';
import { TournamentType } from '@/types/tournament.model';
import {
  getTournamentStatus,
  getStatusLabel,
  getStatusColor,
  isTournamentCancelled,
} from '@/utils/tournament-status';

interface TournamentDetailsHeaderProps {
  tournament: Tournament;
  onDeleteClick: () => void;
  onEditNameClick: () => void;
  isEditMode: boolean;
  onToggleEditMode: () => void;
}

export function TournamentDetailsHeader({
  tournament,
  onDeleteClick,
  onEditNameClick,
  isEditMode,
  onToggleEditMode,
}: TournamentDetailsHeaderProps) {
  const tournamentStatus = getTournamentStatus(tournament);
  const cancelled = isTournamentCancelled(tournament);
  const isStrictTournament = tournament.type === TournamentType.STRICT;

  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-bold leading-tight">
            {tournament.name}
          </h1>
          <Badge
            variant="outline"
            className={`pointer-events-none h-7 text-sm font-medium ${getStatusColor(tournamentStatus)}`}
          >
            {getStatusLabel(tournamentStatus)}
          </Badge>
          {isStrictTournament && (
            <div className="flex h-7 items-center">
              <div className="rounded-full bg-black/70 p-1.5">
                <Lock size={14} className="text-white" />
              </div>
            </div>
          )}
          {!cancelled && tournament.inPause && (
            <Badge
              variant="outline"
              className="pointer-events-none h-7 text-sm"
            >
              Pausado
            </Badge>
          )}
          {isEditMode && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onEditNameClick}
              className="h-8 w-8"
              aria-label="Editar nombre del torneo"
            >
              <PencilIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        {!cancelled && (
          <Button
            variant={isEditMode ? 'outline' : 'default'}
            onClick={onToggleEditMode}
            className="flex items-center gap-2"
          >
            {isEditMode ? (
              <>
                <XIcon className="h-4 w-4" />
                Cancelar Edición
              </>
            ) : (
              <>
                <EditIcon className="h-4 w-4" />
                Editar
              </>
            )}
          </Button>
        )}
        {!cancelled && (
          <Button
            variant="destructive"
            onClick={onDeleteClick}
            className="flex items-center gap-2"
          >
            <Trash2Icon className="h-4 w-4" />
            Cancelar Torneo
          </Button>
        )}
      </div>
    </div>
  );
}
